import {
  ARCHIVED_INSTALL_KEY,
  COUNTER_KEYS,
  MAX_TRACKED_INSTALLS,
  SOLO_BOARD_SIZE,
  emptyCounters,
  naturalSoloId,
  recomputeXp,
  type CounterKey,
  type CounterMap,
  type InstallInfo,
  type PlayerProfile,
  type SoloScore,
} from "./profile";

/**
 * Merging two profiles into one that has lost nothing.
 *
 * This is the whole cloud-save design in one function (LIVE_SERVICES_PLAN.md
 * §B4, docs/CLOUD_SAVES_DESIGN.md §4). It must be:
 *
 *   commutative — merge(a, b) == merge(b, a)
 *   associative — grouping of three profiles doesn't matter
 *   idempotent  — merge(a, a) == a, so a retried sync can't inflate anything
 *
 * Those three properties are what let the sync engine push whole state inside a
 * retrying transaction and know it converges, with no operation log, no offline
 * queue and no lost-write window. Every rule below is chosen to keep them, so
 * anything added here needs the same test treatment (mergeProfiles.test.ts).
 *
 * Pure: no I/O, no clock reads. "Now" arrives as an argument.
 */
export function mergeProfiles(a: PlayerProfile, b: PlayerProfile, nowMs: number): PlayerProfile {
  const installs = mergeInstalls(a.installs, b.installs);
  const counters = emptyCounters();
  const archivedBaselines = emptyCounters();
  for (const key of COUNTER_KEYS) {
    const { live, baselines } = mergeCounter(
      a.stats.counters[key],
      b.stats.counters[key],
      a.stats.archivedBaselines[key],
      b.stats.archivedBaselines[key],
    );
    counters[key] = live;
    archivedBaselines[key] = baselines;
  }
  foldOverflow(counters, archivedBaselines, installs);

  const merged: PlayerProfile = {
    schemaVersion: Math.max(a.schemaVersion, b.schemaVersion),
    stats: {
      xp: 0,
      counters,
      archivedBaselines,
      winStreak: mergeWinStreak(a, b),
      bestWinStreak: Math.max(a.stats.bestWinStreak, b.stats.bestWinStreak),
      longestChain: Math.max(a.stats.longestChain, b.stats.longestChain),
      bestCardsInTurn: Math.max(a.stats.bestCardsInTurn, b.stats.bestCardsInTurn),
      bestFiveOutsInRound: Math.max(a.stats.bestFiveOutsInRound, b.stats.bestFiveOutsInRound),
      bestOffByOnesInRound: Math.max(a.stats.bestOffByOnesInRound, b.stats.bestOffByOnesInRound),
      bestStarsInRound: Math.max(a.stats.bestStarsInRound, b.stats.bestStarsInRound),
      bestStarsInOneCard: Math.max(a.stats.bestStarsInOneCard, b.stats.bestStarsInOneCard),
      bestMeteorDestroyed: Math.max(a.stats.bestMeteorDestroyed, b.stats.bestMeteorDestroyed),
      bestDestroyedInTurn: Math.max(a.stats.bestDestroyedInTurn, b.stats.bestDestroyedInTurn),
      bestWildKindsInTurn: Math.max(a.stats.bestWildKindsInTurn, b.stats.bestWildKindsInTurn),
      bestEventsInTurn: Math.max(a.stats.bestEventsInTurn, b.stats.bestEventsInTurn),
      bestLocksInRound: Math.max(a.stats.bestLocksInRound, b.stats.bestLocksInRound),
      bestTurnsAlightInRound: Math.max(a.stats.bestTurnsAlightInRound, b.stats.bestTurnsAlightInRound),
      starfallEventsSeen: unionFlags(a.stats.starfallEventsSeen, b.stats.starfallEventsSeen),
      aiDefeated: unionFlags(a.stats.aiDefeated, b.stats.aiDefeated),
      playerCountsPlayed: unionFlags(a.stats.playerCountsPlayed, b.stats.playerCountsPlayed),
      onlineWinTableSizes: unionFlags(a.stats.onlineWinTableSizes, b.stats.onlineWinTableSizes),
      deckSizesPlayed: unionFlags(a.stats.deckSizesPlayed, b.stats.deckSizesPlayed),
      soloDeckSizesCompleted: unionFlags(a.stats.soloDeckSizesCompleted, b.stats.soloDeckSizesCompleted),
      venuesPlayed: unionFlags(a.stats.venuesPlayed, b.stats.venuesPlayed),
    },
    achievements: mergeAchievements(a.achievements, b.achievements),
    // Same rule and for the same reason: the earliest round is the one that
    // took part, and a union can only ever add an event, never lose one.
    events: mergeAchievements(a.events, b.events),
    // And again: the first clear is the one that happened, and a union means a
    // level cleared on two devices before they synced is still one clear, so
    // it pays its XP once (recomputeXp counts keys, never adds).
    campaign: mergeAchievements(a.campaign, b.campaign),
    // A best only ever rises, so the larger is the one that happened on some
    // device; a replay on one device can never lower another's.
    campaignBest: maxMerge(a.campaignBest, b.campaignBest),
    soloScores: mergeSoloBoards(a.soloScores, b.soloScores),
    installs,
    mergedAtMs: nowMs,
    // The round-id lists never sync (§B2) — they exist to stop a refresh farming
    // roundsPlayed on THIS device. The caller passes the local profile as `a`,
    // so `a`'s lists are the ones that mean anything here.
    countedRoundIds: [...a.countedRoundIds],
    completedRoundIds: [...a.completedRoundIds],
  };
  merged.stats.xp = recomputeXp(merged);
  return merged;
}

/**
 * One metric. Per-install `max` is safe because an install only ever writes its
 * own key, so that value is monotonically increasing — retrying a sync, or
 * receiving the same state twice, changes nothing.
 */
function mergeCounter(
  liveA: CounterMap = {},
  liveB: CounterMap = {},
  baseA: CounterMap = {},
  baseB: CounterMap = {},
): { live: CounterMap; baselines: CounterMap } {
  const live = maxMerge(liveA, liveB);
  const baselines = maxMerge(baseA, baseB);

  let baselineSum = 0;
  for (const [id, folded] of Object.entries(baselines)) {
    baselineSum += folded;
    // A stale peer can still be carrying a key for an install that has already
    // been folded away. Anything at or below the baseline is already inside
    // `_archived`; dropping it is what stops the fold double counting.
    const value = live[id];
    if (value !== undefined && value <= folded) delete live[id];
  }

  // `_archived` is kept equal to the sum of the baselines rather than merged as
  // a scalar: two profiles that folded DIFFERENT installs each hold part of the
  // archive, and a `max` of the two totals would silently drop one side's.
  const archived = Math.max(live[ARCHIVED_INSTALL_KEY] ?? 0, baselineSum);
  if (archived > 0) live[ARCHIVED_INSTALL_KEY] = archived;
  else delete live[ARCHIVED_INSTALL_KEY];

  return { live, baselines };
}

/**
 * §B3's growth guard. Beyond 25 live install keys, fold the least-recently-active
 * installs into `_archived` and record what each was worth at the fold, so a
 * later merge can tell a stale key from an install that has genuinely returned.
 *
 * A real player has one to three installs — this is a safety valve for someone
 * who clears storage weekly, not an expected path, which is why it is allowed to
 * be lossy in the one corner §B3 signs off on (a year-dormant install that comes
 * back with new progress over-counts its pre-fold total once).
 */
function foldOverflow(
  counters: Record<CounterKey, CounterMap>,
  baselines: Record<CounterKey, CounterMap>,
  installs: Record<string, InstallInfo>,
): void {
  const liveIds = new Set<string>();
  for (const key of COUNTER_KEYS) {
    for (const id of Object.keys(counters[key])) if (id !== ARCHIVED_INSTALL_KEY) liveIds.add(id);
  }
  if (liveIds.size <= MAX_TRACKED_INSTALLS) return;

  const victims = [...liveIds]
    // Ties broken by id so the choice is a function of the merged state alone —
    // otherwise merge(a, b) and merge(b, a) could archive different installs.
    .sort((x, y) => (installs[x]?.lastActiveMs ?? 0) - (installs[y]?.lastActiveMs ?? 0) || (x < y ? -1 : 1))
    .slice(0, liveIds.size - MAX_TRACKED_INSTALLS);

  for (const id of victims) {
    for (const key of COUNTER_KEYS) {
      const value = counters[key][id];
      if (value === undefined) continue;
      delete counters[key][id];
      baselines[key][id] = Math.max(baselines[key][id] ?? 0, value);
    }
    delete installs[id];
  }

  for (const key of COUNTER_KEYS) {
    let sum = 0;
    for (const folded of Object.values(baselines[key])) sum += folded;
    const archived = Math.max(counters[key][ARCHIVED_INSTALL_KEY] ?? 0, sum);
    if (archived > 0) counters[key][ARCHIVED_INSTALL_KEY] = archived;
  }
}

function maxMerge(x: CounterMap, y: CounterMap): CounterMap {
  const out: CounterMap = { ...x };
  for (const [key, value] of Object.entries(y)) out[key] = Math.max(out[key] ?? 0, value);
  return out;
}

function unionFlags(x: Record<string, boolean>, y: Record<string, boolean>): Record<string, boolean> {
  const out: Record<string, boolean> = { ...x };
  for (const [key, value] of Object.entries(y)) out[key] = !!out[key] || !!value;
  return out;
}

/** Union, keeping the earliest unlock — that is the one that actually happened. */
function mergeAchievements(x: Record<string, number>, y: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = { ...x };
  for (const [id, at] of Object.entries(y)) out[id] = id in out ? Math.min(out[id], at) : at;
  return out;
}

function mergeInstalls(x: Record<string, InstallInfo>, y: Record<string, InstallInfo>): Record<string, InstallInfo> {
  const out: Record<string, InstallInfo> = {};
  for (const [id, info] of Object.entries(x)) out[id] = { lastActiveMs: info.lastActiveMs };
  for (const [id, info] of Object.entries(y)) {
    out[id] = { lastActiveMs: Math.max(out[id]?.lastActiveMs ?? 0, info.lastActiveMs) };
  }
  return out;
}

/**
 * The current streak is the one field with no correct merge: it is live session
 * state, not an accumulation. Summing is nonsense and `max` is worse — it would
 * let a streak already broken on your phone come back to life from a stale
 * laptop. Last-writer-wins by install activity is the least-wrong option, and
 * `bestWinStreak` (a `max`) is what actually needs preserving (§B2).
 */
function mergeWinStreak(a: PlayerProfile, b: PlayerProfile): number {
  const activeA = lastActiveMs(a);
  const activeB = lastActiveMs(b);
  if (activeA === activeB) {
    // Neither side is demonstrably newer, and picking one by argument order
    // would make the merge non-commutative.
    return Math.max(a.stats.winStreak, b.stats.winStreak);
  }
  return activeA > activeB ? a.stats.winStreak : b.stats.winStreak;
}

function lastActiveMs(profile: PlayerProfile): number {
  let latest = 0;
  for (const info of Object.values(profile.installs)) latest = Math.max(latest, info.lastActiveMs);
  return latest;
}

function mergeSoloBoards(
  x: Record<string, SoloScore[]>,
  y: Record<string, SoloScore[]>,
): Record<string, SoloScore[]> {
  const out: Record<string, SoloScore[]> = {};
  for (const key of new Set([...Object.keys(x), ...Object.keys(y)])) {
    const byId = new Map<string, SoloScore>();
    for (const entry of [...(x[key] ?? []), ...(y[key] ?? [])]) {
      const id = entry.id || naturalSoloId(entry);
      const existing = byId.get(id);
      byId.set(id, existing ? canonical(existing, { ...entry, id }) : { ...entry, id });
    }
    out[key] = [...byId.values()]
      // Sorted by id last so entries tying on turns AND timestamp still land in
      // one fixed order, whichever way round the two profiles arrived.
      .sort((p, q) => p.turns - q.turns || p.playedAtMs - q.playedAtMs || (p.id < q.id ? -1 : p.id > q.id ? 1 : 0))
      .slice(0, SOLO_BOARD_SIZE);
  }
  return out;
}

/**
 * Two entries under one id are normally byte-identical copies of the same run.
 * They can differ only when the natural-key fallback pairs two runs that share a
 * turn count and a millisecond, so this picks between them by content rather
 * than by which side was passed first — again, commutativity.
 */
function canonical(p: SoloScore, q: SoloScore): SoloScore {
  // One bit per kind, so no two different wild mixes rank equal. Smash and
  // Lightning keep their order relative to each other from before Swap and Peek.
  const rank = (s: SoloScore) => (s.smash ? 8 : 0) + (s.lightning ? 4 : 0) + (s.swap ? 2 : 0) + (s.peek ? 1 : 0);
  return rank(p) <= rank(q) ? p : q;
}
